import React, { useState } from "react";
import { observer } from "mobx-react-lite";
import { Route, Routes, useNavigate, useLocation, useParams } from "react-router-dom";
import { BreadCrumb } from "primereact/breadcrumb";
import Dashboard from "./pages/Dashboard";
import WorkflowEditor from "./pages/WorkflowEditor";
import MyTemplates from "./pages/MyTemplates";
import SharedTemplates from "./pages/SharedTemplates";
import ToolDetail from "./pages/ToolDetail";
import RunHistory from "./pages/RunHistory";
import ResultsBrowser from "./pages/ResultsBrowser";
import tools from "./components/ToolRegistry";
import "./Screening.css";

// ── Palette node definitions (draggable to canvas) ──
const PALETTE_NODES = {
  input: [
    { id: "input-molecules", icon: "📥", label: "化合物库", paletteType: "input" },
    { id: "input-protein", icon: "🧬", label: "蛋白结构", paletteType: "input" },
  ],
  flow: [
    { id: "filter", icon: "🎯", label: "分数筛选", paletteType: "filter" },
    { id: "aggregate", icon: "🔗", label: "结果合并", paletteType: "aggregate" },
    { id: "output", icon: "📤", label: "输出结果", paletteType: "output" },
  ],
};

// ── Sidebar nav items ──
const NAV_ITEMS = [
  { id: "dashboard", label: "Dashboard", icon: "🏠", route: "" },
  { type: "divider" },
  { id: "my-templates", label: "My Templates", icon: "📋", route: "library/mine" },
  { id: "shared-templates", label: "Shared Templates", icon: "🌐", route: "library/shared" },
  { type: "divider" },
  { id: "palette-input", label: "Input", icon: "📥", children: PALETTE_NODES.input.map((n) => ({
    ...n, route: null,
  })) },
  { id: "tools", label: "Tools", icon: "🔧", children: tools.map((t) => ({
    id: t.id, label: t.name, icon: t.icon, route: `tools/${t.id}`, isTool: true,
  })) },
  { id: "palette-flow", label: "Flow Control", icon: "🎯", children: PALETTE_NODES.flow.map((n) => ({
    ...n, route: null,
  })) },
  { type: "divider" },
  { id: "runs", label: "Run History", icon: "▶️", route: "runs" },
  { id: "results", label: "Results", icon: "📊", route: "results" },
];

const Screening = () => {
  const { id: projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Derive active nav from current path
  const path = location.pathname.replace(`/project/${projectId}/screening`, "").replace(/^\//, "");
  const [expandedSections, setExpandedSections] = useState({
    tools: true,
    "palette-input": true,
    "palette-flow": true,
  });

  const toggleSection = (id) => {
    setExpandedSections((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const isActive = (route) => {
    if (!route) return path === "";
    return path.startsWith(route);
  };

  const handleNav = (route) => {
    if (route) navigate(route);
  };

  const handleNewWorkflow = () => {
    navigate("editor/new");
  };

  // ── Drag from sidebar onto the canvas ──
  const onToolDragStart = (event, item) => {
    let data;
    if (item.isTool) {
      data = tools.find((t) => t.id === item.id);
    } else {
      data = { id: item.paletteType, icon: item.icon, name: item.label, paletteType: item.paletteType };
    }
    if (data) {
      event.dataTransfer.setData("application/reactflow", JSON.stringify(data));
      event.dataTransfer.effectAllowed = "move";
    }
  };

  const renderNavItem = (item) => {
    if (item.type === "divider") {
      return <div key={Math.random()} className="screening-nav-divider" />;
    }

    if (item.children) {
      const isExpanded = expandedSections[item.id] ?? true;
      return (
        <div key={item.id} className="screening-nav-group">
          <div
            className="screening-nav-header"
            onClick={() => toggleSection(item.id)}
          >
            <span>{item.icon} {item.label}</span>
            <span className={`screening-nav-arrow ${isExpanded ? "expanded" : ""}`}>▶</span>
          </div>
          {isExpanded && item.children.map((child) => (
            <div
              key={child.id}
              className={`screening-nav-item screening-nav-child ${isActive(child.route) ? "active" : ""}`}
              draggable
              onDragStart={(e) => onToolDragStart(e, child)}
              onClick={() => handleNav(child.route)}
            >
              <span className="screening-nav-icon">{child.icon}</span>
              <span>{child.label}</span>
            </div>
          ))}
        </div>
      );
    }

    return (
      <div
        key={item.id}
        className={`screening-nav-item ${isActive(item.route) ? "active" : ""}`}
        onClick={() => handleNav(item.route)}
      >
        <span className="screening-nav-icon">{item.icon}</span>
        <span>{item.label}</span>
      </div>
    );
  };

  return (
    <div className="screening-layout">
      {/* ── Sidebar ── */}
      <div className="screening-sidebar">
        <div className="screening-sidebar-title">
          <span>🔬 Screening</span>
        </div>
        <div style={{ padding: "4px 12px 8px" }}>
          <div
            className="screening-nav-item"
            style={{ background: "linear-gradient(135deg, #6366f1, #4f46e5)", color: "#fff", fontWeight: 500, justifyContent: "center" }}
            onClick={() => handleNewWorkflow()}
          >
            <span>+ New Workflow</span>
          </div>
        </div>
        <div className="screening-sidebar-nav">
          {NAV_ITEMS.map(renderNavItem)}
        </div>
      </div>

      {/* ── Content ── */}
      <div className="screening-content">
        <ScreeningBreadcrumb path={path} projectId={projectId} tools={tools} />
        <Routes>
          <Route index element={<Dashboard projectId={projectId} />} />
          <Route path="editor/:workflowId" element={<WorkflowEditor projectId={projectId} />} />
          <Route path="library/mine" element={<MyTemplates projectId={projectId} />} />
          <Route path="library/shared" element={<SharedTemplates projectId={projectId} />} />
          <Route path="tools/:toolId" element={<ToolDetail projectId={projectId} />} />
          <Route path="runs" element={<RunHistory projectId={projectId} />} />
          <Route path="results" element={<ResultsBrowser projectId={projectId} />} />
        </Routes>
      </div>
    </div>
  );
};

export default observer(Screening);

// ── Breadcrumb ──
const BREADCRUMB_MAP = {
  "": "Dashboard",
  "library/mine": "My Templates",
  "library/shared": "Shared Templates",
  runs: "Run History",
  results: "Results",
};

const ScreeningBreadcrumb = ({ path, projectId, tools }) => {
  const navigate = useNavigate();
  const parts = path.split("/").filter(Boolean);
  if (parts.length === 0 || path.startsWith("editor/")) return null;

  const items = [
    { label: "Screening", command: () => navigate("") },
  ];

  if (parts[0] === "tools" && parts[1]) {
    const tool = tools.find((t) => t.id === parts[1]);
    items.push({ label: "Tools", command: () => navigate("tools/" + parts[1]) });
    items.push({ label: tool?.name || parts[1] });
  } else if (parts[0] === "library") {
    items.push({ label: parts[1] === "mine" ? "My Templates" : "Shared Templates" });
  } else {
    const label = BREADCRUMB_MAP[path] || path;
    items.push({ label });
  }

  return (
    <div style={{ padding: "8px 16px 0" }}>
      <BreadCrumb model={items} />
    </div>
  );
};
